import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, ArrowDown, Phone, MapPin, Sparkles, ChevronRight } from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import CinematicHeroBackground from '../components/CinematicHeroBackground';
import { Reveal, StaggerContainer, staggerItem } from '../components/Motion';
import Marquee from '../components/Marquee';
import { COMPANY, SERVICES, MANIFESTO, REASONS, REALISATIONS, IMG } from '../data/site';
import { HERO, HOMEPAGE } from '../constants/testIds';

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const yTitle = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <>
      {/* HERO — cinematic */}
      <section ref={heroRef} data-testid={HERO.root} className="hero-media relative min-h-[100svh] w-full overflow-hidden bg-black">
        <CinematicHeroBackground
          src={IMG.heroDoor}
          alt="Marquise en fer forgé et porte haussmannienne — Paris"
        />

        {/* Content */}
        <motion.div style={{ y: yTitle }} className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-48 pb-12">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-1 hidden lg:flex flex-col items-start gap-4">
              <div className="h-px w-8 bg-white/40" />
              <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/60 -rotate-90 origin-top-left translate-y-24">Paris · 2025</span>
            </div>

            <div className="col-span-12 lg:col-span-11">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.3, duration: 0.6 }}
                className="mb-6 flex items-center gap-3"
              >
                <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/60">◍ Groupe Ponthieu</span>
              </motion.div>

              <h1 data-testid={HERO.title} className="font-display italic tracking-tight leading-[0.9] text-[13vw] sm:text-[11vw] lg:text-[9vw] text-white">
                <span className="clip-reveal"><span style={{ animationDelay: '2.3s' }}>Serrurerie,</span></span>
                <span className="clip-reveal"><span style={{ animationDelay: '2.45s' }}>métallerie</span></span>
                <span className="clip-reveal"><span style={{ animationDelay: '2.6s' }}>& vitrerie</span></span>
              </h1>

              <motion.p
                data-testid={HERO.subtitle}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 3.0, duration: 0.8 }}
                className="mt-8 md:mt-12 max-w-xl font-mono text-[13px] leading-relaxed text-white/70 tracking-tight"
              >
                Installation, rénovation et dépannage pour particuliers, professionnels
                et copropriétés. Un savoir-faire artisanal au cœur de Paris.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 3.25, duration: 0.8 }}
                className="mt-10 flex flex-wrap gap-3"
              >
                <Link
                  to="/contact"
                  data-testid={HERO.ctaQuote}
                  className="shine-wrap inline-flex items-center gap-3 bg-white text-black px-6 h-12 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/90"
                >
                  Demander un devis <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
                <a
                  href={`tel:${COMPANY.phoneTel}`}
                  data-testid={HERO.ctaCall}
                  className="shine-wrap inline-flex items-center gap-3 border border-white/25 text-white px-6 h-12 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/10"
                >
                  <Phone size={14} strokeWidth={1.5} /> Appeler maintenant
                </a>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Bottom bar with meta */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3.4, duration: 0.6 }}
          className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 backdrop-blur-md"
        >
          <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-6 font-mono text-[10px] tracking-[0.22em] uppercase text-white/60">
            <div className="hidden md:flex items-center gap-3"><MapPin size={12} strokeWidth={1.5} /> {COMPANY.address} — {COMPANY.city}</div>
            <div className="flex items-center gap-2 animate-pulse"><ArrowDown size={12} strokeWidth={1.5} /> Défiler</div>
            <div className="hidden md:block">Serrurerie · Métallerie · Vitrerie · Dépannage</div>
          </div>
        </motion.div>
      </section>

      {/* Marquee editorial */}
      <Marquee items={['Serrurerie', 'Métallerie', 'Vitrerie', 'Dépannage']} />

      {/* Présentation rapide */}
      <section className="relative py-24 md:py-40 overflow-hidden">
        <AnimatedBackground />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <Reveal>
              <div className="overline mb-4">◍ Ponthieu — Paris & Île-de-France</div>
              <h2 className="font-display italic text-5xl md:text-6xl leading-[0.95]">
                Un <em className="not-italic underline decoration-white/30 underline-offset-4">interlocuteur unique</em> pour toutes vos réalisations.
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.15}>
              <p className="text-white/70 text-lg leading-relaxed">
                <span className="text-white">Ponthieu Pose et Rénovation</span> accompagne les particuliers, les
                professionnels et les copropriétés parisiennes pour la pose, la rénovation, le
                dépannage et les ouvrages sur mesure.
              </p>
              <p className="text-white/60 text-lg leading-relaxed mt-6">
                Notre exigence : un travail fiable, précis et durable — quels que soient l’échelle
                du projet et les contraintes du lieu.
              </p>
              <div className="mt-10 h-px w-full bg-white/10" />
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                {['Serrurerie', 'Métallerie', 'Vitrerie', 'Dépannage'].map((k, i) => (
                  <div key={k} className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/70">
                    <div className="text-white/40 mb-1">0{i + 1}</div>
                    {k}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Services bento */}
      <section data-testid={HOMEPAGE.services} className="relative py-24 md:py-32 overflow-hidden bg-ink-900">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-12 gap-6 items-end mb-14">
            <div className="col-span-12 md:col-span-6">
              <div className="overline mb-4">◍ Nos métiers</div>
              <h2 className="font-display italic text-5xl md:text-6xl leading-[0.95]">Ce que nous savons faire.</h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8">
              <p className="text-white/60">
                Quatre spécialités complémentaires, exercées dans le même atelier, pour
                garantir la cohérence de chaque intervention.
              </p>
            </div>
          </div>

          <StaggerContainer className="grid grid-cols-12 gap-4 md:gap-6">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.slug}
                variants={staggerItem}
                data-testid={HOMEPAGE.serviceCard(s.slug)}
                className={`relative ${i === 0 ? 'col-span-12 md:col-span-8 md:row-span-2 aspect-[16/11] md:aspect-auto md:min-h-[540px]' : 'col-span-12 md:col-span-4 aspect-[4/3]'}`}
              >
                <Link to={`/${s.slug}`} className="hero-media group block relative w-full h-full overflow-hidden bg-black border border-white/10 hover:border-white/30 transition-colors">
                  <img
                    src={s.cover}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-[1.04] transition-[transform,opacity] duration-[900ms] ease-out"
                    style={{ filter: 'grayscale(30%) contrast(1.05)' }}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/60">{s.index} · {s.kicker}</span>
                      <ArrowUpRight size={22} strokeWidth={1.25} className="text-white/70 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                    <div>
                      <h3 className="font-display italic text-4xl md:text-5xl leading-none">{s.title}</h3>
                      <p className="mt-3 text-sm text-white/70 max-w-md">{s.homeSubtitle || s.baseline}</p>
                      <div className="mt-6 font-mono text-[10px] tracking-[0.22em] uppercase text-white/80 border-b border-white/25 pb-1 inline-block">Découvrir →</div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Manifesto numbered chapters */}
      <section className="relative py-24 md:py-40 overflow-hidden">
        <AnimatedBackground intensity="strong" />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="overline mb-4">◍ Notre exigence</div>
            <h2 className="font-display italic text-5xl md:text-6xl leading-[0.95] max-w-3xl">
              Un travail fiable, précis et durable.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.08}>
                <div className="flex items-start gap-6 md:gap-10">
                  <div className="font-display italic text-7xl md:text-8xl leading-none text-white/85">{m.n}</div>
                  <div>
                    <div className="overline mb-3">Chapitre {m.n}</div>
                    <h3 className="font-display italic text-3xl md:text-4xl leading-tight">{m.label}</h3>
                    <p className="mt-4 text-white/65 leading-relaxed max-w-md">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi nous */}
      <section data-testid={HOMEPAGE.reasons} className="relative py-24 md:py-32 border-y border-white/10 bg-ink-900">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-12 gap-6 mb-12">
            <div className="col-span-12 md:col-span-5">
              <div className="overline mb-4">◍ Pourquoi nous choisir</div>
              <h2 className="font-display italic text-5xl md:text-6xl leading-[0.95]">Le sens du détail.</h2>
            </div>
          </div>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {REASONS.map((r) => (
              <motion.div key={r.title} variants={staggerItem} className="bg-ink-900 p-8 md:p-10">
                <Sparkles size={16} strokeWidth={1.25} className="text-white/80" />
                <h3 className="mt-6 font-display italic text-2xl md:text-3xl leading-tight">{r.title}</h3>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">{r.body}</p>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="relative py-24 md:py-32 border-y border-white/10 bg-ink-900 overflow-hidden">
        <AnimatedBackground />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 md:col-span-5">
            <div className="overline mb-4">◍ Zone d’intervention</div>
            <h2 className="font-display italic text-5xl md:text-6xl leading-[0.95]">Paris & Île-de-France.</h2>
            <p className="mt-6 text-white/60 text-lg max-w-md leading-relaxed">
              Nous intervenons dans l’ensemble de Paris et de l’Île-de-France pour vos poses,
              rénovations et dépannages.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6 font-mono text-[10px] tracking-[0.22em] uppercase text-white/70">
              <div><div className="text-white/40 mb-1">01</div>Paris 1–20</div>
              <div><div className="text-white/40 mb-1">02</div>Hauts-de-Seine</div>
              <div><div className="text-white/40 mb-1">03</div>Île-de-France</div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <div className="hero-media relative aspect-[4/3] overflow-hidden glass border border-white/10">
              <img src="/zone-intervention.png" alt="Camionnette Ponthieu à Paris — Tour Eiffel et carte de l’Île-de-France" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: 'center' }} />
              <div className="absolute inset-0 bg-black/10" />
              <div className="absolute inset-6 border border-white/15" />
              <div className="absolute left-6 top-6 font-mono text-[10px] tracking-[0.22em] uppercase text-white/70">Paris</div>
              <div className="absolute right-6 bottom-6 font-mono text-[10px] tracking-[0.22em] uppercase text-white/70">Île-de-France</div>
            </div>
          </div>
        </div>
      </section>

      {/* Appel à l'action final */}
      <section data-testid={HOMEPAGE.cta} className="relative py-32 md:py-48 overflow-hidden">
        <AnimatedBackground intensity="strong" />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 text-center">
          <div className="overline mb-6">◍ On en parle</div>
          <h2 className="font-display italic text-[13vw] sm:text-[10vw] lg:text-[8vw] leading-[0.9]">
            Un projet ou <em className="not-italic underline decoration-white/40 underline-offset-8">une urgence</em> ?
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-white/70 text-lg leading-relaxed">
            Contactez Ponthieu Pose et Rénovation pour obtenir une étude personnalisée
            ou organiser une intervention.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <a href={`tel:${COMPANY.phoneTel}`} className="shine-wrap inline-flex items-center gap-2 border border-white/25 px-6 h-12 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/10">
              <Phone size={14} strokeWidth={1.5} /> Appeler le {COMPANY.phone}
            </a>
            <Link to="/contact" className="shine-wrap inline-flex items-center gap-2 bg-white text-black px-6 h-12 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/90">
              Demander un devis <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
